import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Card, Col, Row, Typography, Space, Tag, Divider, Empty, Button, App } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
const { Title, Text } = Typography;
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

import OrderDetails from "./OrderDetails";
import { useAntdTheme } from "../../shared/hooks";

import { getFetch, API_URL } from "../../shared/utils/Util";

interface ResultsLocationState {
  query?: string;
  orderDate?: string | null;
}

const localTime = (t: Date): string => {
  return t.toLocaleString("en-ZA", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
};

const OrderLookupResults: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();

  const restaurantId = sessionStorage.getItem("restaurant_id");
  if (!restaurantId) {
    return <Navigate to="/login" replace />;
  }

  const incomingState = location.state as ResultsLocationState | null;
  const query = incomingState?.query ?? "";
  const orderDate: Dayjs | null = incomingState?.orderDate ? dayjs(incomingState.orderDate) : null;

  const [orders, setOrders] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(true);

  const goBack = () => {
    navigate("/order-lookup", {
      state: {
        query,
        orderDate: orderDate ? orderDate.format() : null,
        fromResults: true,
      },
    });
  };

  useEffect(() => {
    if (!query.trim()) {
      setIsSearching(false);
      setOrders([]);
      return;
    }

    setIsSearching(true);
    let url = `${API_URL}/v1/r/${restaurantId}/pos_order_names/${query}`;
    if (orderDate) {
      url += "?ts=" + encodeURIComponent(orderDate.format());
    }

    getFetch(
      url,
      (result: any) => {
        setIsSearching(false);
        if (result.hasOwnProperty("orders") && result.orders && result.orders.length > 0) {
          setOrders(result.orders);
        } else {
          setOrders([]);
        }
      },
      () => {
        setIsSearching(false);
        notification.error({
          message: t("error"),
          description: t("error_occurred"),
        });
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderCard = (order: any) => {
    try {
      return (
        <Card
          key={order.id}
          style={{
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadowSecondary,
            marginBottom: token.marginLG,
            border: `1px solid ${token.colorBorder}`,
            overflow: "hidden",
          }}
          styles={{ body: { padding: token.paddingXL } }}
        >
          {/* Order Header */}
          <Space direction="vertical" style={{ width: "100%", gap: token.marginMD }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={4} style={{ margin: 0, color: token.colorPrimary }}>
                  {order.pos_order_name}
                  <Tag
                    style={{
                      marginLeft: token.marginSM,
                      fontSize: token.fontSizeSM,
                      padding: `${token.paddingXS} ${token.marginSM}`,
                      borderRadius: token.borderRadius,
                    }}
                    color="blue"
                  >
                    {order.pos_order_type}
                  </Tag>
                </Title>
              </Col>
              <Col>
                {order.paid ? (
                  <Tag
                    icon={<CheckCircleOutlined />}
                    color="success"
                    style={{
                      fontSize: token.fontSize,
                      padding: `${token.paddingXS} ${token.marginMD}`,
                      borderRadius: token.borderRadius,
                      fontWeight: token.fontWeightStrong,
                    }}
                  >
                    {t("paid")}
                  </Tag>
                ) : (
                  <Tag
                    icon={<CloseCircleOutlined />}
                    color="error"
                    style={{
                      fontSize: token.fontSize,
                      padding: `${token.paddingXS} ${token.marginMD}`,
                      borderRadius: token.borderRadius,
                      fontWeight: token.fontWeightStrong,
                    }}
                  >
                    {t("not_paid")}
                  </Tag>
                )}
              </Col>
            </Row>

            <Divider style={{ margin: `${token.marginSM} 0` }} />

            {/* Time Information */}
            <Row gutter={[16, 16]}>
              {order.create_time && (
                <Col xs={24} sm={8}>
                  <Space>
                    <CalendarOutlined
                      style={{
                        color: token.colorPrimary,
                        fontSize: token.fontSize,
                      }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                        {t("order_time")}
                      </Text>
                      <Text strong style={{ fontSize: token.fontSizeSM }}>
                        {localTime(new Date(order.create_time))}
                      </Text>
                    </div>
                  </Space>
                </Col>
              )}
              {order.start_time && (
                <Col xs={24} sm={8}>
                  <Space>
                    <CalendarOutlined
                      style={{
                        color: token.colorWarning,
                        fontSize: token.fontSize,
                      }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                        {t("start_time")}
                      </Text>
                      <Text strong style={{ fontSize: token.fontSizeSM }}>
                        {localTime(new Date(order.start_time))}
                      </Text>
                    </div>
                  </Space>
                </Col>
              )}
              {order.done_time && (
                <Col xs={24} sm={8}>
                  <Space>
                    <CheckCircleOutlined
                      style={{
                        color: token.colorSuccess,
                        fontSize: token.fontSize,
                      }}
                    />
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                        {t("done_time")}
                      </Text>
                      <Text strong style={{ fontSize: token.fontSizeSM }}>
                        {localTime(new Date(order.done_time))}
                      </Text>
                    </div>
                  </Space>
                </Col>
              )}
            </Row>

            {/* Customer Information */}
            {order.customer && (
              <>
                <Divider style={{ margin: `${token.marginSM} 0` }} />
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={8}>
                    <Space>
                      <UserOutlined style={{ color: token.colorPrimary }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                          {t("name")}
                        </Text>
                        <Text strong copyable>
                          {order.customer.name}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Space>
                      <MailOutlined style={{ color: token.colorPrimary }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                          {t("email")}
                        </Text>
                        <Text strong copyable>
                          {order.customer.email}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Space>
                      <PhoneOutlined style={{ color: token.colorPrimary }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                          {t("phone")}
                        </Text>
                        <Text strong copyable>
                          {order.customer.phone}
                        </Text>
                      </div>
                    </Space>
                  </Col>
                </Row>
              </>
            )}

            <Divider style={{ margin: `${token.marginMD} 0` }} />

            {/* Order Details */}
            <OrderDetails order={order} pending={false} restaurantId={restaurantId} />
          </Space>
        </Card>
      );
    } catch (err) {
      console.log("error displaying order search result", err);
      return null;
    }
  };

  const cards = orders.map(orderCard);

  return (
    <Space direction="vertical" style={{ width: "100%", gap: token.marginLG }}>
      {/* Back button + search summary */}
      <Card
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadow,
          background: token.colorBgContainer,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Space direction="vertical" style={{ width: "100%", gap: token.marginMD }}>
          {/* Search term, big and centered at the top */}
          <div style={{ textAlign: "center" }}>
            <Title
              level={2}
              style={{
                margin: 0,
                color: token.colorPrimary,
                fontSize: token.fontSizeHeading1,
                fontWeight: token.fontWeightStrong,
                wordBreak: "break-word",
              }}
            >
              {query}
            </Title>
            {orderDate && (
              <Tag
                color="default"
                style={{
                  marginTop: token.marginSM,
                  fontSize: token.fontSizeLG,
                  padding: `${token.paddingXS} ${token.marginMD}`,
                }}
              >
                {orderDate.format("ddd, MMM DD")}
              </Tag>
            )}
          </div>

          {/* Back button, centered below the search term */}
          <div style={{ textAlign: "center" }}>
            <Button
              type="primary"
              icon={<ArrowLeftOutlined />}
              size="large"
              onClick={goBack}
              style={{ borderRadius: token.borderRadiusLG }}
            >
              {t("back_to_search")}
            </Button>
          </div>
        </Space>
      </Card>

      {/* Results Section */}
      {isSearching ? (
        <Card
          style={{
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadow,
            textAlign: "center",
            padding: token.paddingXL,
          }}
        >
          <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
            {t("searching")}
          </Text>
        </Card>
      ) : orders.length === 0 ? (
        <Card
          style={{
            borderRadius: token.borderRadiusLG,
            boxShadow: token.boxShadow,
            textAlign: "center",
            padding: token.paddingXL,
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical">
                <Text type="secondary" style={{ fontSize: token.fontSizeLG }}>
                  {t("no_results")}
                </Text>
                <Text type="secondary">{t("try_searching_again")}</Text>
              </Space>
            }
          />
        </Card>
      ) : (
        <div>{cards}</div>
      )}
    </Space>
  );
};

export default OrderLookupResults;
